import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useAnimation } from 'framer-motion'

interface UseCodeInputParams {
  /** Количество ячеек кода */
  length: number
  /** Колбэк вызывается с полным кодом, как только все ячейки заполнены */
  onComplete: (code: string) => void | Promise<void>
  /** При увеличении значения — триггерит shake-анимацию и очищает инпут */
  shakeSignal: number
  /** При увеличении значения — очищает ячейки и фокусирует первую (без shake) */
  resetSignal?: number
}

interface UseCodeInputReturn {
  /** Значения цифр по ячейкам */
  digits: string[]
  /** Массив рефов на инпуты */
  inputsRef: React.MutableRefObject<Array<HTMLInputElement | null>>
  /** AnimationControls для shake-обёртки */
  shakeControls: ReturnType<typeof useAnimation>
  /** Обработчик изменения одной ячейки */
  handleChange: (index: number, value: string) => void
  /** Обработчик клавиши (для Backspace между ячейками) */
  handleKeyDown: (index: number, event: KeyboardEvent<HTMLInputElement>) => void
  /** Обработчик paste: распределяет вставленный код по ячейкам */
  handlePaste: (event: ClipboardEvent<HTMLInputElement>) => void
}

/**
 * Логика группы инпутов для ввода кода: автопереход между ячейками,
 * paste-распределение, автоматическая отправка при заполнении,
 * shake-анимация и очистка при смене shakeSignal.
 * @param params.length - Количество ячеек
 * @param params.onComplete - Колбэк при заполнении всех ячеек
 * @param params.shakeSignal - Триггер shake-анимации + очистки
 * @returns Состояние и обработчики для рендера ячеек
 */
export const useCodeInput = ({ length, onComplete, shakeSignal, resetSignal = 0 }: UseCodeInputParams): UseCodeInputReturn => {
  const [digits, setDigits] = useState<string[]>(() => Array(length).fill(''))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const shakeControls = useAnimation()

  useEffect(() => {
    inputsRef.current[0]?.focus()
  }, [])

  // Resend: чистим ячейки и возвращаем фокус на первую (без shake-анимации).
  useEffect(() => {
    if (resetSignal > 0) {
      setDigits(Array(length).fill(''))
      inputsRef.current[0]?.focus()
    }
    // length стабилен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal])

  useEffect(() => {
    if (shakeSignal > 0) {
      void shakeControls.start({
        x: [0, -10, 10, -8, 8, -4, 4, 0],
        transition: { duration: 0.4 }
      })
      setDigits(Array(length).fill(''))
      inputsRef.current[0]?.focus()
    }
    // shakeControls и length стабильны
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shakeSignal])

  const tryComplete = (next: string[]) => {
    if (next.every((d) => d.length === 1)) {
      void onComplete(next.join(''))
    }
  }

  const handleChange = (index: number, value: string) => {
    const onlyDigits = value.replace(/\D/g, '')

    // iOS/Android autofill (autocomplete="one-time-code") вставляет весь код
    // в одну ячейку — распределяем его по всем, начиная с текущей.
    if (onlyDigits.length > 1) {
      const next = [...digits]
      for (let i = 0; i < onlyDigits.length && index + i < length; i++) {
        next[index + i] = onlyDigits[i]
      }
      setDigits(next)
      const emptyIdx = next.findIndex((d) => !d)
      const focusIdx = emptyIdx === -1 ? length - 1 : emptyIdx
      inputsRef.current[focusIdx]?.focus()
      tryComplete(next)
      return
    }

    const cleaned = onlyDigits.slice(0, 1)
    const next = [...digits]
    next[index] = cleaned
    setDigits(next)
    if (cleaned && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
    tryComplete(next)
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    event.preventDefault()
    const next = Array(length).fill('').map((_, i) => pasted[i] ?? '')
    setDigits(next)
    const emptyIdx = next.findIndex((d) => !d)
    const focusIdx = emptyIdx === -1 ? length - 1 : emptyIdx
    inputsRef.current[focusIdx]?.focus()
    tryComplete(next)
  }

  return { digits, inputsRef, shakeControls, handleChange, handleKeyDown, handlePaste }
}
